import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, ArrowRight, Star } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6 } }) };

const TIERS = [
  { id: "personal", name: "Personal Use", price: "$15", desc: "For personal, non-commercial use only" },
  { id: "commercial", name: "Commercial License", price: "$75", desc: "For business, marketing, and promotional use" },
  { id: "exclusive", name: "Exclusive Rights", price: "$250", desc: "Full exclusive usage rights transferred to buyer" },
];

export default function MarketplacePage() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tier, setTier] = useState("personal");
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/marketplace").then(r => setItems(r.data.items || [])).catch(() => {});
  }, []);

  const purchase = async () => {
    if (!selected || !buyerName || !buyerEmail) { toast.error("Fill in all fields."); return; }
    setLoading(true);
    try {
      const res = await api.post("/marketplace/purchase", {
        media_id: selected.id, license_type: tier,
        buyer_name: buyerName, buyer_email: buyerEmail,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) { toast.error(err.response?.data?.detail || "Purchase failed."); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="marketplace-page" className="pt-20 min-h-screen bg-luxury-black">
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" className="text-center mb-16">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-4">Content Marketplace</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-4xl sm:text-5xl font-light text-white">License Premium <span className="italic text-gold">Event Media</span></motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/40 font-body text-sm mt-4 max-w-lg mx-auto">Purchase high-resolution, professionally enhanced event photography for personal or commercial use.</motion.p>
          </motion.div>

          {items.length === 0 ? (
            <div className="text-center py-20">
              <ShoppingBag size={48} className="text-white/10 mx-auto mb-4" />
              <p className="text-white/30 font-body text-sm">No items available yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {items.map((item, i) => (
                <motion.div key={item.id} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} className={`group cursor-pointer overflow-hidden border transition-all ${selected?.id === item.id ? "border-gold" : "border-white/5 hover:border-white/10"}`} onClick={() => setSelected(item)}>
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img src={item.url} alt={item.caption || item.filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    {item.ai_score && <div className="absolute top-2 right-2 bg-black/60 px-2 py-0.5 flex items-center gap-1"><Star size={10} className="text-gold fill-gold" /><span className="text-gold text-[10px] font-body">{Math.round(item.ai_score)}</span></div>}
                  </div>
                  {item.caption && <p className="p-2 text-white/40 text-[10px] font-body truncate">{item.caption}</p>}
                </motion.div>
              ))}
            </div>
          )}

          {/* Purchase Panel */}
          {selected && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-12 border border-gold/20 bg-luxury-charcoal p-8 max-w-2xl mx-auto" data-testid="purchase-panel">
              <div className="flex gap-6 flex-col sm:flex-row">
                <img src={selected.url} alt="" className="w-full sm:w-40 h-40 object-cover shrink-0" />
                <div className="flex-1">
                  <p className="text-white font-body text-sm font-medium mb-1">{selected.caption || selected.filename}</p>
                  <div className="grid grid-cols-3 gap-2 my-4">
                    {TIERS.map(t => (
                      <button key={t.id} onClick={() => setTier(t.id)} className={`p-3 border text-center transition-all ${tier === t.id ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-white/40 hover:border-white/20"}`} data-testid={`tier-${t.id}`}>
                        <p className="text-xs font-body font-medium">{t.price}</p>
                        <p className="text-[9px] font-body mt-1">{t.name}</p>
                      </button>
                    ))}
                  </div>
                  <input value={buyerName} onChange={e => setBuyerName(e.target.value)} placeholder="Your Name" className="w-full bg-white/5 border border-white/10 text-white text-xs px-3 py-2 mb-2 font-body focus:outline-none focus:border-gold/30" data-testid="buyer-name" />
                  <input value={buyerEmail} onChange={e => setBuyerEmail(e.target.value)} placeholder="Your Email" className="w-full bg-white/5 border border-white/10 text-white text-xs px-3 py-2 mb-4 font-body focus:outline-none focus:border-gold/30" data-testid="buyer-email" />
                  <button onClick={purchase} disabled={loading} className="w-full bg-gold text-black font-body font-medium text-sm py-3 hover:bg-gold-light transition-colors disabled:opacity-40" data-testid="purchase-btn">
                    {loading ? "Processing..." : `Purchase ${TIERS.find(t => t.id === tier)?.price} License`}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
